import * as React from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {DataTable} from "@/lib/components/DataTable";
import {CellContext, ColumnDef} from "@tanstack/react-table";
import {Badge, Box, Grid2, Tooltip} from "@mui/material";
import {OrderFilter, OrderOverviewRowDTO, OrderStatus} from "@/model/order";
import {OrderSortField} from "@/api/data-contracts";
import {Constants} from "@/api/constants";
import {OrderStatusBadge} from "@/common/order/OrderStatusBadge";
import {CommonI18N, OrderI18N, RequestI18N} from "@/translations";
import {AppPaths} from "@/app/AppPaths";
import {ChatBubbleOutlineOutlined} from "@mui/icons-material";
import {TOOLTIP_DELAY_MS} from "@/constants";
import {TFunction} from "i18next";
import {DateFormatter} from "@/lib/components/DateFormate";
import {dateRangeField} from "@/common/fields/date-range-field";
import dayjs from "dayjs";
import {assertNever} from "@/lib/assert-never";
import {formatAddressToSingleLine} from "@/model/address";
import {UseOrderOverviewStateResult} from "@/common/overview-state/useOrderOverviewState";
import {SortDirection} from "@/common/overview-state/overview-state-model";
import {ORDER_FILTER_FIELD_GROUPS} from "@/common/overview-state/order-view-state";
import {activeFilterColumnIds, countDeviatingFilterGroups} from "@/common/overview-state/filter-fields";
import {OverviewFilterBar} from "@/common/overview-table/OverviewFilterBar";
import {OverviewPagination} from "@/common/overview-table/OverviewPagination";
import {renderOrderColumnFilter} from "@/pages/order-tracking-page/OrderColumnFilters";
import {ORDER_TRACKING_COLUMN_IDS} from "@/common/overview-table/overview-table-column-ids";
import {UnreadTotalHeader} from "@/common/overview-table/UnreadTotalHeader";

/**
 * Renders an unread-message badge cell for the info column.
 * Returns null when there are no unread messages so the cell stays empty.
 */
function renderUnreadBadgeCell(t: TFunction) {
    return function UnreadBadgeCell({row}: CellContext<OrderOverviewRowDTO, unknown>) {
        const unreadCount = Math.max(0, row.original.oneToOneUnreadCount ?? 0);
        if (unreadCount === 0) {
            return null;
        }
        return (
                <Tooltip
                        title={t(RequestI18N.details.tooltip.unreadMessages, {count: unreadCount})}
                        enterDelay={TOOLTIP_DELAY_MS}
                        enterNextDelay={TOOLTIP_DELAY_MS}
                        arrow
                >
                <span>
                    <Badge badgeContent={unreadCount} color="error">
                        <ChatBubbleOutlineOutlined fontSize="small"/>
                    </Badge>
                </span>
                </Tooltip>
        );
    };
}

/**
 * Renders the order status cell with a styled badge.
 */
function renderStatusCell({getValue}: CellContext<OrderOverviewRowDTO, unknown>): React.ReactNode {
    return <OrderStatusBadge status={getValue<OrderStatus>()}/>;
}

/**
 * Status column shared by every OrderFilter variant.
 */
function buildStatusColumn(t: TFunction): ColumnDef<OrderOverviewRowDTO> {
    return {
        accessorKey: ORDER_TRACKING_COLUMN_IDS.status,
        header: t(OrderI18N.table.column.status),
        meta: {sortField: OrderSortField.STATUS, filterColumnId: ORDER_TRACKING_COLUMN_IDS.status},
        cell: renderStatusCell
    };
}

/**
 * Delivery address column shared by every OrderFilter variant.
 *
 * The address is rendered through the shared formatter so part order and country name
 * follow the display language (see ADR-012) instead of a hardcoded German composition.
 */
function buildDeliveryAddressColumn(t: TFunction, locale: string): ColumnDef<OrderOverviewRowDTO> {
    return {
        id: ORDER_TRACKING_COLUMN_IDS.deliveryAddress,
        accessorKey: "deliveryAddress",
        header: t(OrderI18N.table.column.deliveryAddress),
        meta: {
            sortField: OrderSortField.DELIVERY_ADDRESS,
            filterColumnId: ORDER_TRACKING_COLUMN_IDS.deliveryAddress
        },
        cell: ({ getValue }) => {
            const addr = getValue<OrderOverviewRowDTO["deliveryAddress"]>();
            if (!addr) return null;
            return formatAddressToSingleLine(t, addr, locale);
        }
    };
}

/**
 * Planned delivery dates (target range) column shared by every OrderFilter variant.
 */
function buildPlannedDeliveryDatesColumn(t: TFunction, dateFormat: string): ColumnDef<OrderOverviewRowDTO> {
    return {
        id: ORDER_TRACKING_COLUMN_IDS.plannedDeliveryDates,
        header: t(OrderI18N.table.column.plannedDeliveryDates),
        meta: {
            sortField: OrderSortField.PLANNED_DELIVERY_PERIOD,
            filterColumnId: ORDER_TRACKING_COLUMN_IDS.plannedDeliveryDates
        },
        cell: ({ row }) =>
            dateRangeField(
                row.original.deliveryDateFrom,
                row.original.deliveryDateTo,
                t(OrderI18N.table.column.plannedDeliveryDates),
                "",
                dateFormat
            ).value || null
    };
}

/**
 * Confirmed delivery date column shared by every OrderFilter variant.
 */
function buildConfirmedDeliveryDateColumn(t: TFunction, dateFormat: string): ColumnDef<OrderOverviewRowDTO> {
    return {
        accessorKey: ORDER_TRACKING_COLUMN_IDS.deliveryDate,
        header: t(OrderI18N.table.column.confirmedDeliveryDate),
        meta: {
            sortField: OrderSortField.CONFIRMED_DELIVERY_DATE,
            filterColumnId: ORDER_TRACKING_COLUMN_IDS.deliveryDate
        },
        cell: ({ getValue }) => (getValue<string>() ? dayjs(getValue<string>()).format(dateFormat) : null)
    };
}

/**
 * Info column (unread-message badge) — only rendered for INTERNAL/EXTERNAL, never for NEUTRAL. Carries no
 * `meta.sortField` — the info column is never sortable (see the plan's "Standing constraints"). Carries
 * `meta.filterColumnId` since Phase 4.2, so the column header renders the `hasUnreadMessages` tri-state
 * filter (`OrderColumnFilters.tsx`); the `NEUTRAL` view never calls `buildInfoColumn` at all, so it keeps
 * no Info column and therefore no filter. The header itself carries `totalUnreadMessages` since Phase 5.4
 * (`UnreadTotalHeader`), the sum over the whole filtered set rather than the current page.
 */
function buildInfoColumn(t: TFunction, totalUnreadMessages: number): ColumnDef<OrderOverviewRowDTO> {
    return {
        id: ORDER_TRACKING_COLUMN_IDS.info,
        header: () => (
            <UnreadTotalHeader label={t(OrderI18N.table.column.info)} total={totalUnreadMessages} />
        ),
        meta: {
            disableTooltip: true,
            filterColumnId: ORDER_TRACKING_COLUMN_IDS.info
        },
        cell: renderUnreadBadgeCell(t)
    };
}

export interface OrderTrackingTableProps {
    type: OrderFilter;
    /**
     * The full overview-state result (#1607) — replaces the previous `statuses`/`onChangeStatuses`/`data`
     * props. The table reads rows, paging and sort straight from it and wires the filter bar, column
     * filters and pagination controls to its mutators.
     */
    state: UseOrderOverviewStateResult;
    /**
     * When true the component fills its parent height and lets the DataTable
     * scroll internally so that stickyHeader works correctly. Default: false.
     */
    fillHeight?: boolean;
}

/**
 * Pure UI component for rendering an order tracking table with the shared filter bar, sortable/
 * filterable column headers and page navigation (#1607). Filter/sort/paging state is controlled
 * externally via `state` so it can be shared between embedded and fullscreen views.
 */
export const OrderTrackingTable: React.FC<OrderTrackingTableProps> = ({ type, state, fillHeight = false }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dateFormat = DateFormatter();

    const columns: ColumnDef<OrderOverviewRowDTO>[] = React.useMemo(() => {
        const statusColumn = buildStatusColumn(t);
        const deliveryAddressColumn = buildDeliveryAddressColumn(t, i18n.language);
        const plannedDeliveryDatesColumn = buildPlannedDeliveryDatesColumn(t, dateFormat);
        const confirmedDeliveryDateColumn = buildConfirmedDeliveryDateColumn(t, dateFormat);

        switch (type) {
            case OrderFilter.INTERNAL:
                return [
                    buildInfoColumn(t, state.totalUnreadMessages),
                    {
                        id: ORDER_TRACKING_COLUMN_IDS.orderNumber,
                        accessorKey: "orderNumber",
                        header: t(OrderI18N.table.column.orderNumber),
                        meta: {
                            sortField: OrderSortField.ORDER_NUMBER,
                            filterColumnId: ORDER_TRACKING_COLUMN_IDS.orderNumber
                        },
                        cell: ({ getValue }) => getValue<string>()
                    },
                    {
                        id: ORDER_TRACKING_COLUMN_IDS.quoteNumber,
                        accessorKey: "externalReferenceNumber",
                        header: t(OrderI18N.table.column.quoteNumber),
                        meta: {
                            sortField: OrderSortField.QUOTE_NUMBER,
                            filterColumnId: ORDER_TRACKING_COLUMN_IDS.quoteNumber
                        },
                        cell: ({ getValue }) => getValue<string>() ?? t(CommonI18N.message.notGiven)
                    },
                    statusColumn,
                    deliveryAddressColumn,
                    plannedDeliveryDatesColumn,
                    confirmedDeliveryDateColumn
                ];
            case OrderFilter.NEUTRAL:
                return [
                    {
                        id: ORDER_TRACKING_COLUMN_IDS.orderNumber,
                        accessorKey: "orderNumber",
                        header: t(OrderI18N.table.column.orderNumber),
                        meta: {
                            sortField: OrderSortField.ORDER_NUMBER,
                            filterColumnId: ORDER_TRACKING_COLUMN_IDS.orderNumber
                        },
                        cell: ({ getValue }) => getValue<string>()
                    },
                    {
                        id: ORDER_TRACKING_COLUMN_IDS.quoteNumber,
                        accessorKey: "quoteReferenceNumber",
                        header: t(OrderI18N.table.column.quoteNumber),
                        meta: {
                            sortField: OrderSortField.QUOTE_NUMBER,
                            filterColumnId: ORDER_TRACKING_COLUMN_IDS.quoteNumber
                        },
                        cell: ({ getValue }) => getValue<string>() ?? t(CommonI18N.message.notGiven)
                    },
                    {
                        accessorKey: "requesterCompanyName",
                        header: t(OrderI18N.table.column.requesterCompanyName),
                        meta: {sortField: OrderSortField.REQUESTER_COMPANY},
                        cell: ({ getValue }) => getValue<string>()
                    },
                    {
                        accessorKey: "supplierCompanyName",
                        header: t(OrderI18N.table.column.supplierCompanyName),
                        meta: {sortField: OrderSortField.SUPPLIER_COMPANY},
                        cell: ({ getValue }) => getValue<string>()
                    },
                    statusColumn,
                    deliveryAddressColumn,
                    plannedDeliveryDatesColumn,
                    confirmedDeliveryDateColumn
                ];
            case OrderFilter.EXTERNAL:
                return [
                    buildInfoColumn(t, state.totalUnreadMessages),
                    {
                        id: ORDER_TRACKING_COLUMN_IDS.orderNumber,
                        accessorKey: "myReferenceNumber",
                        header: t(OrderI18N.table.column.orderNumber),
                        meta: {
                            sortField: OrderSortField.ORDER_NUMBER,
                            filterColumnId: ORDER_TRACKING_COLUMN_IDS.orderNumber
                        },
                        cell: ({ getValue }) => getValue<string>()
                    },
                    {
                        id: ORDER_TRACKING_COLUMN_IDS.quoteNumber,
                        accessorKey: "quoteReferenceNumber",
                        header: t(OrderI18N.table.column.quoteNumber),
                        meta: {
                            sortField: OrderSortField.QUOTE_NUMBER,
                            filterColumnId: ORDER_TRACKING_COLUMN_IDS.quoteNumber
                        },
                        cell: ({ getValue }) => getValue<string>() ?? t(CommonI18N.message.notGiven)
                    },
                    statusColumn,
                    deliveryAddressColumn,
                    plannedDeliveryDatesColumn,
                    confirmedDeliveryDateColumn
                ];
        }
        return assertNever(type);
    }, [t, type, dateFormat, i18n.language, state.totalUnreadMessages]);

    const handleRowClick = React.useCallback(
        (row: OrderOverviewRowDTO) => {
            if (!row.requestId) {
                return;
            }
            switch (type) {
                case OrderFilter.INTERNAL:
                case OrderFilter.NEUTRAL:
                    navigate(AppPaths.INTERNAL_REQUEST_fn(row.requestId));
                    return;
                case OrderFilter.EXTERNAL:
                    navigate(AppPaths.EXTERNAL_REQUEST_fn(row.requestId));
                    return;
            }
            assertNever(type);
        },
        [navigate, type]
    );

    /**
     * `DataTable#onSortChange` is generic over `meta.sortField` (a plain `string`, #1607) — it has no
     * knowledge of `OrderSortField`. Every column here sets `meta.sortField` exclusively to `OrderSortField`
     * enum members (never a string literal), so the value this callback ever receives from `DataTable` is
     * always a genuine `OrderSortField`; the cast simply names that fact at the boundary `state.setSort`
     * enforces.
     */
    const handleSortChange = React.useCallback(
        (field: string | undefined, direction: SortDirection | undefined) =>
            state.setSort(field as OrderSortField | undefined, direction),
        [state]
    );

    const { badgeCount, activeColumnIds } = React.useMemo(
        () => ({
            badgeCount: countDeviatingFilterGroups(state.applied.filters, state.defaults, ORDER_FILTER_FIELD_GROUPS),
            activeColumnIds: activeFilterColumnIds(state.applied.filters, state.defaults, ORDER_FILTER_FIELD_GROUPS)
        }),
        [state.applied.filters, state.defaults]
    );

    // Renders the filter content for a given column id, passed to `DataTableColumnHeader` per column;
    // filtering happens through the column headers only, `OverviewFilterBar` carries no filter fields.
    const renderColumnFilter = React.useCallback(
        (columnId: string) => renderOrderColumnFilter(columnId, state.applied.filters, state.setFilters),
        [state.applied.filters, state.setFilters]
    );

    const filterBar = (
        <OverviewFilterBar
            searchText={state.applied.searchText}
            onSearchTextChange={state.setSearchText}
            searchTextMaxLength={Constants.ORDER_SEARCH_TEXT_LENGTH_MAX}
            badgeCount={badgeCount}
            onReset={state.resetAll}
        />
    );

    const pagination = (
        <OverviewPagination
            page={state.page}
            rowsPerPage={state.size}
            totalElements={state.totalElements}
            totalPages={state.totalPages}
            rowsPerPageOptions={state.sizeOptions}
            onPageChange={state.setPage}
            onRowsPerPageChange={state.setSize}
        />
    );

    const table = (
        <DataTable
            columns={columns}
            data={state.rows}
            noResultsValue={t(OrderI18N.table.message.noOrders)}
            striped
            maxHeight={fillHeight ? "100%" : undefined}
            onRowClick={handleRowClick}
            sortField={state.applied.sortField}
            sortDirection={state.applied.sortDirection}
            onSortChange={handleSortChange}
            renderColumnFilter={renderColumnFilter}
            activeFilterColumnIds={activeColumnIds}
        />
    );

    if (fillHeight) {
        return (
            <Box display="flex" flexDirection="column" height="100%" minHeight={0} gap={2} paddingTop={1}>
                {filterBar}
                <Box flex={1} minHeight={0} width="100%">
                    {table}
                </Box>
                {pagination}
            </Box>
        );
    }

    return (
        <Grid2 container direction="column" spacing={4} paddingTop={1}>
            <Grid2 sx={{position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper"}}>
                {filterBar}
            </Grid2>
            <Grid2 overflow="auto" width="100%">
                {table}
            </Grid2>
            <Grid2>
                {pagination}
            </Grid2>
        </Grid2>
    );
};
